import { Box, Button, Modal, Typography, Paper, Chip } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Fragment } from "react";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { getTeamUrlV2, viewLogsUrl } from "src/react-query/endPoints";
import { fetchInfinite } from "src/react-query/fetchWithError";
import { yearFirstDateTimeFormat2 } from "src/utils/formatTime";
import InfiniteScroll from "react-infinite-scroll-component";
import { Icon } from "@iconify/react";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "900px",
  bgcolor: "background.paper",
  borderRadius: 1,
  boxShadow: 24,
  p: 4,
  overflow: "scroll",
  height: "500px",
};

export const ViewLogsModal = ({ open, onClose, chargerId }) => {
  const { teamId } = useSelectedTeam();
  const { data, hasNextPage, fetchNextPage } = useInfiniteQuery(
    ["viewLogs", { url: getTeamUrlV2(viewLogsUrl(chargerId)) }],
    fetchInfinite,
    {
      getNextPageParam: (lastPage, pages) => {
        if (lastPage.length === 0) {
          return;
        }
        return pages.length + 1;
      },
    }
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box id="scrollableDiv" sx={style}>
        <LogsData data={data} fetchNextPage={fetchNextPage} hasNextPage={hasNextPage}/>
        <Button
          variant="outlined"
          onClick={onClose}
          size="large"
          sx={{ marginTop: 3 }}
        >
          Close
        </Button>
      </Box>
    </Modal>
  );
};


export const LogsData = ({data,fetchNextPage,hasNextPage})=>{
  const theme = useTheme();
  const logBodyBg = theme.palette.mode === "dark"
    ? alpha(theme.palette.common.white, 0.06)
    : theme.palette.grey[100];

  const getDirectionChipStyles = (direction) => {
    const isIncoming = direction === "INCOMING";
    const colorSource = isIncoming ? theme.palette.info : theme.palette.success;
    return {
      bgcolor: alpha(colorSource.main, theme.palette.mode === "dark" ? 0.35 : 0.15),
      color: theme.palette.mode === "dark" ? colorSource.light : colorSource.dark,
      borderColor: alpha(colorSource.main, 0.4),
      borderWidth: 1,
      borderStyle: "solid",
      fontWeight: 600,
    };
  };

  const getStatusChipStyles = (isSuccess) => {
    const paletteSource = isSuccess ? theme.palette.success : theme.palette.error;
    return {
      bgcolor: alpha(paletteSource.main, theme.palette.mode === "dark" ? 0.4 : 0.18),
      color: theme.palette.mode === "dark" ? paletteSource.light : paletteSource.dark,
      borderColor: alpha(paletteSource.main, 0.6),
      borderWidth: 1,
      borderStyle: "solid",
      fontWeight: 600,
    };
  };

  const getMessageTypeLabel = (message) => {
    try {
        const parsedMessage = JSON.parse(message);
        const messageType = typeof parsedMessage?.[2] === "string" 
          ? parsedMessage[2] 
          : "UNKNOWN";
        return messageType;
    } catch (error) {
      return "INVALID";
    }
  }

  const getErrorStatus = (message) => {
    try {
      const parsedMessage = JSON.parse(message);
      const errorCode = parsedMessage?.[3]?.errorCode;
      if(errorCode !== undefined && errorCode !== "NoError"){
        return false;
      }
      return true;
    } catch (error) {
      return "Invalid";
    }
  }
  return(
    <>
      <Typography variant="h6" mb={2}>OCPP Communication Logs ({data?.pages?.flat()?.length ?? 0} entries)</Typography>
      <Box
        id="scrollableDiv"
        sx={{
          height: '500px',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <InfiniteScroll
          dataLength={data?.pages?.flat()?.length ?? 0}
          next={fetchNextPage}
          hasMore={hasNextPage}
          loader={
            <Typography sx={{ textAlign: "center", mt: 2 }}>
              Loading more logs...
            </Typography>
          }
          scrollableTarget="scrollableDiv"
        >
          {Array.isArray(data?.pages) && data.pages.length > 0 && data?.pages.map((page, index) => (
            <Fragment key={index}>
              {page?.map((log, index) => {
                const { messageTime, messageType, message } = log;
                const isSuccess = !!getErrorStatus(message);
                return (
              <Box sx={{ mt: 2, p: 2 }} border={1} borderColor="divider" borderRadius={1} key={index}>
                <Box mt={2}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Chip 
                        label={messageType} 
                        sx={getDirectionChipStyles(messageType)}
                        size="small" 
                      />
                      <Chip label={`${getMessageTypeLabel(message)}`} size="small" />
                      <Chip
                          label={isSuccess ? "SUCCESS" : "ERROR"}
                        size="small"
                        icon={
                          <Icon 
                            icon={isSuccess ? "mdi:check-circle" : "mdi:close-circle"}
                            fontSize="small"
                            color={isSuccess ? theme.palette.success.main : theme.palette.error.main}
                          />
                        }
                          sx={getStatusChipStyles(isSuccess)}
                      />
                      <Box ml="auto">
                        <Typography variant="body2" color="text.secondary">
                          {yearFirstDateTimeFormat2(messageTime)}
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      component="pre"
                      sx={{
                        whiteSpace: "pre-wrap",
                        bgcolor: logBodyBg,
                        wordWrap: "break-word",
                        borderRadius: 1,
                        p: 2,
                        color: "text.primary",
                        fontFamily: 'Consolas, "SFMono-Regular", "Courier New", monospace',
                      }}
                    >
                      {message}
                    </Box>
                  </Paper>
                </Box>
              </Box>
                );
              })}
            </Fragment>
          ))}
        </InfiniteScroll>
        </Box>
    </>
  )
}

export const OldLogsData = ({data,fetchNextPage,hasNextPage})=>{
  return(
    <InfiniteScroll
          dataLength={data?.pages?.flat()?.length ?? 0}
          next={fetchNextPage}
          hasMore={hasNextPage}
          loader={<h4>Loading more logs...</h4>}
          scrollableTarget="scrollableDiv"
        >
          {data?.pages.map((page, index) => (
            <Fragment key={index}>
              {page?.map(({ messageTime, messageType, message }, index) => (
                <Box mb={2} key={index}>
                  <Typography
                    variant="caption"
                    gutterBottom
                    sx={{
                      color: messageType === "INCOMING" ? "blue" : "green",
                      marginRight: "10px",
                      fontSize: "16px",
                    }}
                  >
                    {messageType}:
                  </Typography>
                  <Typography
                    variant="caption"
                    gutterBottom
                    sx={{ marginRight: "10px", fontSize: "16px" }}
                  >
                    {yearFirstDateTimeFormat2(messageTime)} -
                  </Typography>

                  <Typography
                    variant="caption"
                    gutterBottom
                    sx={{ fontSize: "16px", wordWrap: "break-word" }}
                  >
                    {message}
                  </Typography>
                </Box>
              ))}
            </Fragment>
          ))}
        </InfiniteScroll>
  )
}